import type { PickListPick } from '@/infrastructure/FulfillmentAdminClient';
import { kstLabelStamp } from '@/utils/fulfillmentTime';
import {
  BLANK,
  ROWS_PER_SHEET,
  blankAsDash,
  nameLinesOf,
  paginateByHeight,
  rowUnits,
  sheetDocument,
  sheetHtml,
  type SheetPage,
  type SheetRow,
} from './labelSheet';
import { splitAcrossLocations } from './lineLocations';

/** What the walk leaves off the paper, so the screen can say so before the picker goes looking. */
export interface PickListLeftOff {
  sample: number;
  unshelved: number;
}

const onShelf = (pick: PickListPick): boolean => blankAsDash(pick.location) !== BLANK;

/** One pick per shelf: the server cuts a split line per shelf, and a line it left whole is cut here [SSD-7236]. */
const perShelf = (picks: PickListPick[]): PickListPick[] =>
  picks.flatMap((pick) =>
    splitAcrossLocations(pick, (whole, location) => ({ ...whole, location: location.locationCode, qty: location.qty })),
  );

/**
 * The READY TO BE OUTBOUNDED pick list on the same 150x100 label as PACKING [10/7]: no order number and no
 * barcode, one row per shelf and sku with the units of the whole selection added up, shelves in location
 * order. The sample set is pulled from its own shelf and a line with no shelf cannot be walked to, so
 * neither is on the sheet; the modal names what it left off.
 */
export class PickListSheet {
  public static readonly TITLE = 'READY TO BE OUTBOUNDED';

  /** The picks the sheet carries: shelved merchandise; the sample set and unshelved lines are left off. */
  public static onSheet(picks: PickListPick[]): PickListPick[] {
    return perShelf(picks).filter((pick) => pick.isSample !== true && onShelf(pick));
  }

  public static leftOff(picks: PickListPick[]): PickListLeftOff {
    const shelved = perShelf(picks);
    return {
      sample: shelved.filter((pick) => pick.isSample === true).length,
      unshelved: shelved.filter((pick) => pick.isSample !== true && !onShelf(pick)).length,
    };
  }

  /** The merged, ordered rows. Last-4 and size print as the pick list sends them, a hyphen where it sends none. */
  public static rowsOf(picks: PickListPick[]): SheetRow[] {
    const merged = new Map<string, SheetRow>();
    for (const pick of PickListSheet.onSheet(picks)) {
      const location = blankAsDash(pick.location);
      const key = `${location}\u0000${pick.sku}`;
      const row = merged.get(key);
      if (row) {
        row.qty += pick.qty;
      } else {
        merged.set(key, {
          no: 0,
          eanLast4: blankAsDash(pick.eanLast4),
          name: pick.productName,
          size: blankAsDash(pick.size),
          location,
          qty: pick.qty,
        });
      }
    }
    return [...merged.values()]
      .sort(
        (a, b) => a.location.localeCompare(b.location, undefined, { numeric: true }) || a.name.localeCompare(b.name),
      )
      .map((row, index) => ({ ...row, no: index + 1 }));
  }

  public static pagesOf(picks: PickListPick[]): Array<SheetPage<SheetRow>> {
    return paginateByHeight(PickListSheet.rowsOf(picks), (row) => rowUnits(nameLinesOf(row.name)), ROWS_PER_SHEET);
  }

  /** The sheet as a document of its own, stamped with the moment it was printed (there is no order to date it by). */
  public static htmlOf(picks: PickListPick[], printedAt: Date | string): string {
    const rows = PickListSheet.rowsOf(picks);
    if (rows.length === 0) {
      throw new Error('Nothing on a shelf to pick: the sample set and unshelved lines are not on the sheet.');
    }
    const totalQty = rows.reduce((sum, row) => sum + row.qty, 0);
    const stamp = kstLabelStamp(printedAt instanceof Date ? printedAt.toISOString() : printedAt);
    const sheets = PickListSheet.pagesOf(picks).map((page) =>
      sheetHtml({ title: PickListSheet.TITLE, totalQty }, page, stamp),
    );
    return sheetDocument(PickListSheet.TITLE, sheets);
  }
}
